import { useState } from 'react';

import { brand } from '../../config/brand';
import { useSession } from '../../stores/session';

/**
 * Your link, and how it is going.
 *
 * ## Why it lives inside the boosts dialog
 *
 * Because this is the other way to earn the thing the dialog is about, and a
 * currency explained in one place with a second way of getting it explained
 * somewhere else is a currency nobody understands. Somebody reading "ten good
 * calls is a boost" is exactly the person who wants to know that five friends
 * is one too.
 *
 * ## Two numbers, because the difference is a real question
 *
 * "Joined" is everybody who registered on the link and "confirmed" is those who
 * clicked the link in their email. The gap between them is what somebody who
 * sent it to six people and sees four is looking at, and it is answerable:
 * points arrive when an address is confirmed, not when a form is submitted.
 */
export function ReferralLink({
  joined,
  credited,
  points,
  perReferral,
}: {
  joined: number;
  credited: number;
  points: number;
  /** What one confirmed signup is worth, from the server. */
  perReferral: number;
}) {
  const user = useSession((state) => state.user);
  const [copied, setCopied] = useState(false);

  if (!user) return null;

  /*
   * Built from the address the page is actually on rather than from
   * configuration, so a link copied on a staging host points at that host. The
   * one thing worse than no referral link is one that sends your friends
   * somewhere they cannot sign up.
   */
  const link = `${window.location.origin}/join/${user.username}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /*
       * Clipboard access can be refused - an insecure origin, a permission, a
       * browser that simply will not. The input beside the button is the
       * fallback and is why the link is shown rather than hidden behind the
       * button in the first place.
       */
    }
  };

  return (
    <div className="flex flex-col gap-3" data-referral>
      <div className="flex items-baseline justify-between gap-3">
        <span className="meta-caps text-faint">Or bring somebody</span>
        {joined > 0 && (
          <span className="font-mono text-[10px] text-ghost" data-referral-counts>
            {joined} joined · {credited} confirmed
          </span>
        )}
      </div>

      <p className="text-pretty text-sm leading-relaxed text-soft">
        Anybody who makes an account on your link earns you{' '}
        <span className="text-ink">{perReferral} points</span> once they confirm their email.
        Nothing arrives before that, which is the only thing stopping this being a numbers game.
      </p>

      <div className="flex items-center gap-2">
        {/*
          Readable and selectable, not a button that hides what it is about to
          put on your clipboard. `readOnly` rather than disabled so it can still
          be focused and selected by hand.
        */}
        <input
          readOnly
          value={link}
          data-referral-link
          onFocus={(event) => event.currentTarget.select()}
          className="min-w-0 flex-1 rounded-[var(--radius-control)] border border-line bg-app px-3 py-2 font-mono text-[11px] text-soft outline-none focus:border-line-strong"
        />

        <button
          type="button"
          onClick={() => void copy()}
          data-referral-copy
          className="shrink-0 rounded-[var(--radius-control)] border border-line-strong px-3 py-2 text-[13px] font-medium text-ink transition-colors hover:bg-raised"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      {points > 0 && (
        <p className="font-mono text-[11px] text-faint" data-referral-points>
          {points} points from invitations so far.
        </p>
      )}

      <p className="font-mono text-[10px] leading-relaxed text-ghost">
        It is your profile address with /join in front of it, so anybody can see whose link it is
        before they open it. {brand.name} counts one referrer per person, once.
      </p>
    </div>
  );
}
